/**
 * PATH: packages/email-hub/src/feature/components/ShadowResolutionStats.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-SHADOWRESOLUTIONSTATS-001
 * PURPOSE: Collapsible diagnostics card showing the shadow entity-resolution
 *          stats for the org: link volume, per-type/source/status
 *          breakdowns, confidence buckets, and how the shadow JOB resolver
 *          agrees with the live Email.job_id (agree / live-had-no-job /
 *          disagree). Mounted at the top of the "Recent issues" drawer.
 * WHY:     Phase 4 of the email-intelligence upgrade. Shadow EmailEntityLink
 *          rows now accumulate (EMAIL_AI_V2_SHADOW) without changing
 *          behaviour; this surfaces the evidence we need before flipping
 *          the behaviour-changing EMAIL_AI_V2 flag. Hidden when there's no
 *          shadow data yet, so it adds nothing for orgs not yet collecting.
 * RELATED:
 *   - _internal/services/entityLinkStats.ts: the data source.
 *   - backend organisation/services/email_entity_stats.py: payload shape.
 */

import { useState } from 'react'
import { Activity, ChevronDown, ChevronRight, Loader2 } from 'lucide-react'
import {
  useEntityLinkStats,
  type EntityLinkJobAgreement,
} from '../../_internal/services/entityLinkStats'

/**
 * Pure: one-line verdict on the shadow JOB resolver vs the live path.
 * Input → the agreement block. Output → a short human string.
 * Exported for unit testing.
 */
export function summariseAgreement(a: EntityLinkJobAgreement): string {
  if (!a || a.total === 0) return 'No shadow job links yet'
  const parts: string[] = []
  if (a.agreement_rate != null) {
    parts.push(`${Math.round(a.agreement_rate * 100)}% agree with live`)
  }
  if (a.live_had_no_job > 0) {
    parts.push(`${a.live_had_no_job} new (live had none)`)
  }
  if (a.disagree > 0) {
    parts.push(`${a.disagree} disagree`)
  }
  return parts.join(' · ') || `${a.total} job links`
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex flex-col items-center px-2 py-1.5 rounded bg-foreground/5 dark:bg-white/5 min-w-[64px]">
      <span className="text-base font-semibold tabular-nums">{value}</span>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
    </div>
  )
}

function Breakdown({ title, data }: { title: string; data: Record<string, number> }) {
  const entries = Object.entries(data || {}).filter(([, n]) => n > 0)
  if (!entries.length) return null
  return (
    <div className="text-[11px]">
      <span className="text-muted-foreground">{title}: </span>
      {entries.map(([k, n], i) => (
        <span key={k}>
          {i > 0 && ', '}
          <span className="font-medium">{k}</span> {n}
        </span>
      ))}
    </div>
  )
}

export default function ShadowResolutionStats({ open }: { open: boolean }) {
  const { stats, loading, error } = useEntityLinkStats(30, open)
  const [expanded, setExpanded] = useState(false)

  // Hide entirely when there's no shadow data (or the endpoint is absent):
  // the card must add nothing for orgs not yet collecting.
  const hasData = !!stats && !stats.error && stats.totals?.links > 0
  if (!hasData) {
    if (loading) {
      return (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground px-1 py-1">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading AI resolution stats…
        </div>
      )
    }
    return null
  }

  const s = stats!
  const verdict = summariseAgreement(s.job_agreement)

  return (
    <div className="border border-violet-300/40 dark:border-violet-700/40 bg-violet-500/5 rounded-md p-2.5">
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-2 text-left"
      >
        {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <Activity className="h-3.5 w-3.5 text-violet-600 dark:text-violet-400" />
        <span className="text-xs font-semibold">AI shadow resolution</span>
        <span className="ml-auto text-[11px] text-muted-foreground truncate max-w-[55%]">{verdict}</span>
      </button>

      {expanded && (
        <div className="mt-2.5 space-y-2.5">
          <div className="flex flex-wrap gap-1.5">
            <Stat label="links" value={s.totals.links} />
            <Stat label="emails" value={s.totals.emails_with_links} />
            <Stat label="job links" value={s.job_agreement.total} />
            <Stat label="agree" value={s.job_agreement.agree} />
            <Stat label="new" value={s.job_agreement.live_had_no_job} />
            <Stat label="disagree" value={s.job_agreement.disagree} />
          </div>
          <Breakdown title="By type" data={s.by_type} />
          <Breakdown title="By source" data={s.by_source} />
          <Breakdown title="By status" data={s.by_status} />
          <Breakdown
            title="Confidence"
            data={s.confidence_buckets as unknown as Record<string, number>}
          />
          <p className="text-[10px] text-muted-foreground">
            Shadow window: last {s.window_days} days. Resolution runs in shadow
            (no inbox changes) until validated.
          </p>
          {error && <p className="text-[10px] text-destructive">{error}</p>}
        </div>
      )}
    </div>
  )
}
